import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, firstValueFrom, takeUntil } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { NotaFiscalEntradaService } from '@shared/services/apis/orquestrador-compras/nota-fiscal-entrada.service';
import { DetalheCompraResponse } from '@shared/services/responses/compras/detalhe-compra.response';
import { NotaFiscalEntradaContextoVinculoPedidoResponse } from '@shared/services/responses/orquestrador-compras/nota-fiscal-entrada-contexto-vinculo-pedido.response';
import { UtilService } from '@shared/services/util.service';
import { SeletorNotaFiscalComponent } from '../../../components/seletor-nota-fiscal/seletor-nota-fiscal.component';
import { VinculoNotaPedidoFluxoService } from '../../../services/vinculo-nota-pedido-fluxo.service';
import { KanbanCard } from '../../../processo-compras.models';
import { isItemAssociacaoPendente } from '../../../processo-compras.associacao';
import { formatCodigoCompra } from '../card-edit-dialog.types';

/**
 * Bloco "Nota fiscal do pedido" do card (Faturado, Conferência e Divergência): cartão por NF vinculada com
 * situação do vínculo e da associação, progresso dos itens, saldo do pedido, "Trocar NF" e o seletor de NF.
 */
@Component({
  selector: 'app-ced-stage-nota', standalone: true,
  imports: [
    CommonModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule,
    AlertCardComponent, AbntPipe, SeletorNotaFiscalComponent,
  ],
  templateUrl: './ced-stage-nota.component.html', styleUrl: './ced-stage-nota.component.css',
})
export class CedStageNotaComponent implements OnInit, OnDestroy {
  @Input({ required: true }) card!: KanbanCard;
  @Input() detalhe?: DetalheCompraResponse;
  @Input() somenteLeitura = false;
  /** Conferência cega não concluída: oculta quantidades, saldo, "Abrir nota" e "Rever vínculo". */
  @Input() ocultarQuantidades = false;
  @Output() atualizado = new EventEmitter<void>();
  /** A cada carga das notas (vazia quando falhou): o card no modo NF reaproveita na tabela "Itens da nota". */
  @Output() contextosCarregados = new EventEmitter<NotaFiscalEntradaContextoVinculoPedidoResponse[]>();
  contextos: NotaFiscalEntradaContextoVinculoPedidoResponse[] = [];
  carregando = false;
  erro = '';
  bloqueio = '';
  /** "Trocar NF": mostra o seletor para escolher a nota que substitui a atual. */
  trocandoNota = false;
  private readonly destroy$ = new Subject<void>();
  private destruido = false;

  constructor(private notasService: NotaFiscalEntradaService, private vinculoNotaPedidoFluxo: VinculoNotaPedidoFluxoService, private alert: AlertService) {}
  ngOnInit(): void { void this.carregarNotasVinculadas(); }

  /** Motivo exibido no tooltip das ações de NF quando estão bloqueadas por regra (não durante o carregamento). */
  get motivoAcoesBloqueadas(): string {
    if (this.bloqueio) return this.bloqueio;
    if (this.erro) return this.erro;
    return this.somenteLeitura ? 'Pedido arquivado ou em etapa final: o vínculo com a NF não pode ser alterado.' : '';
  }
  ngOnDestroy(): void { this.destruido = true; this.destroy$.next(); this.destroy$.complete(); }

  /** Nota vinculada com item ainda em associação (`isItemAssociacaoPendente`): o cartão mostra "Associar itens". */
  isAssociacaoPendente(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): boolean {
    return (contexto.itens ?? []).some(isItemAssociacaoPendente);
  }

  /** Pedido com nota vinculada (pelo detalhe, mesmo se o contexto da nota não carregou). */
  get temNota(): boolean {
    return !!this.detalhe?.vinculosNota?.length;
  }

  /** D-01: a linha deste pedido no vínculo da nota tem itens gravados. */
  isVinculoConfirmado(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): boolean {
    return (contexto.vinculosPedido ?? []).some(v => v.idCompra === this.card.idCompra && v.itens?.length > 0);
  }

  /** D-03: itens da NF com vínculo em qualquer pedido ou, com associação pendente, itens já associados. */
  itensProgresso(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): number {
    const itens = contexto.itens ?? [];
    if (this.isAssociacaoPendente(contexto)) return itens.filter(i => !isItemAssociacaoPendente(i)).length;
    const vinculados = new Set((contexto.vinculosPedido ?? []).flatMap(v => (v.itens ?? []).map(l => l.idNFeEntradaItem)));
    return itens.filter(i => vinculados.has(i.idNFeEntradaItem)).length;
  }

  /** Faixa verde: itens associados, vínculo deste pedido gravado e todos os itens da NF vinculados. */
  isTudoVinculado(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): boolean {
    const total = (contexto.itens ?? []).length;
    return !this.isAssociacaoPendente(contexto) && this.isVinculoConfirmado(contexto)
      && total > 0 && this.itensProgresso(contexto) === total;
  }

  /** Outros pedidos vinculados à mesma nota ("Consolidada com #X, #Y"). */
  outrosPedidos(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): string {
    const codigos = (contexto.vinculosPedido ?? [])
      .filter(v => v.idCompra !== this.card.idCompra)
      .map(v => formatCodigoCompra(v.codigoCompra));
    return [...new Set(codigos)].join(', ');
  }

  /** Quantidade vinculada deste pedido na nota. */
  quantidadeVinculadaNota(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): number {
    return (contexto.vinculosPedido ?? [])
      .filter(v => v.idCompra === this.card.idCompra)
      .reduce((total, v) => total + (v.itens ?? []).reduce((soma, l) => soma + (l.quantidadeVinculada ?? 0), 0), 0);
  }

  /** D-04: quantidade vinculada deste pedido em todas as notas. */
  get quantidadeVinculadaPedido(): number {
    return this.contextos.reduce((total, c) => total + this.quantidadeVinculadaNota(c), 0);
  }

  /** D-04: quantidade pedida (soma dos itens do pedido). */
  get quantidadePedida(): number {
    return (this.detalhe?.itens ?? []).reduce((total, i) => total + (i.quantidade ?? 0), 0);
  }

  /** D-04: saldo pendente do pedido; zero ou negativo = saldo zerado. */
  get saldoPedido(): number {
    return UtilService.arredondarAbnt(this.quantidadePedida - this.quantidadeVinculadaPedido, 4);
  }

  /** D-04: unidade exibida só quando todos os itens do pedido têm a mesma. */
  get unidadePedido(): string {
    const unidades = new Set((this.detalhe?.itens ?? []).map(i => i.unidade));
    return unidades.size === 1 ? [...unidades][0] ?? '' : '';
  }

  /** Itens do pedido em unidades diferentes: as quantidades não são somadas, são exibidas por unidade. */
  get isMultiplasUnidades(): boolean {
    return new Set((this.detalhe?.itens ?? []).map(i => i.unidade)).size > 1;
  }

  /** Quantidade vinculada deste pedido na nota, agrupada pela unidade do item do pedido. */
  quantidadeVinculadaNotaPorUnidade(contexto: NotaFiscalEntradaContextoVinculoPedidoResponse): { unidade: string; quantidade: number }[] {
    const unidadeItem = new Map((this.detalhe?.itens ?? []).map(i => [i.idCompraItem, i.unidade || '—']));
    const totais = new Map<string, number>();
    for (const v of (contexto.vinculosPedido ?? []).filter(v => v.idCompra === this.card.idCompra))
      for (const l of v.itens ?? []) {
        const unidade = unidadeItem.get(l.idCompraItem) ?? '—';
        totais.set(unidade, (totais.get(unidade) ?? 0) + (l.quantidadeVinculada ?? 0));
      }
    return [...totais].map(([unidade, quantidade]) => ({ unidade, quantidade }));
  }

  /** Recebido × pedido por unidade (pedido com itens em unidades diferentes). */
  get saldoPorUnidade(): { unidade: string; pedida: number; recebida: number; saldo: number }[] {
    const linhas = new Map<string, { unidade: string; pedida: number; recebida: number; saldo: number }>();
    for (const i of this.detalhe?.itens ?? []) {
      const unidade = i.unidade || '—';
      const linha = linhas.get(unidade) ?? { unidade, pedida: 0, recebida: 0, saldo: 0 };
      linha.pedida += i.quantidade ?? 0;
      linhas.set(unidade, linha);
    }
    for (const c of this.contextos)
      for (const v of this.quantidadeVinculadaNotaPorUnidade(c)) {
        const linha = linhas.get(v.unidade) ?? { unidade: v.unidade, pedida: 0, recebida: 0, saldo: 0 };
        linha.recebida += v.quantidade;
        linhas.set(v.unidade, linha);
      }
    return [...linhas.values()].map(l => ({ ...l, saldo: UtilService.arredondarAbnt(l.pedida - l.recebida, 4) }));
  }

  /** Unidades com saldo pendente, para o texto "saldo pendente de ..." (vazio = saldo zerado). */
  get saldoPendentePorUnidade(): { unidade: string; saldo: number }[] {
    return this.saldoPorUnidade.filter(l => l.saldo > 0);
  }

  /** D-06: só na etapa Faturado, com associação pendente ou vínculo sem linhas em alguma nota. */
  get isAvancoBloqueado(): boolean {
    return this.card.stage === 'faturado'
      && this.contextos.some(c => this.isAssociacaoPendente(c) || !this.isVinculoConfirmado(c));
  }

  /** D-05: abre o cadastro da nota em nova aba. */
  abrirNota(idNFeEntrada: number): void {
    window.open(`${window.location.origin}/suprimentos/nota-fiscal-entrada/documento/${idNFeEntrada}`, '_blank', 'noopener');
  }

  /** Contextos e travas de todas as notas vinculadas, uma chamada de cada, em paralelo. Também recarrega o painel. */
  private async carregarNotasVinculadas(): Promise<void> {
    this.carregando = true;
    this.contextos = [];
    this.erro = '';
    this.bloqueio = '';
    try {
      const ids = [...new Set((this.detalhe?.vinculosNota ?? []).map(v => v.idNFeEntrada))];
      if (!ids.length) return;
      const [contextos, travas] = await Promise.all([
        firstValueFrom(this.notasService.contextosVinculoPedido(ids).pipe(takeUntil(this.destroy$))),
        firstValueFrom(this.notasService.consultarEdicoesVinculo(ids).pipe(takeUntil(this.destroy$))),
      ]);
      if (contextos.isError) throw new Error(contextos.errorDescription);
      const comErro = contextos.lista.find(c => c.isError);
      if (comErro) throw new Error(comErro.errorDescription);
      this.contextos = contextos.lista;
      if (travas.isError) throw new Error(travas.errorDescription);
      this.bloqueio = travas.lista.find(t => t.isBloqueado)?.motivoBloqueio ?? '';
    } catch (e) {
      if (!this.destruido) {
        this.erro = e instanceof Error ? e.message : 'Não foi possível carregar a NF.';
        this.alert.show('NF do pedido', this.erro, AlertType.error);
      }
    }
    finally {
      this.carregando = false;
      if (!this.destruido) this.contextosCarregados.emit(this.contextos);
    }
  }

  private async verificarTrava(): Promise<boolean> {
    if (this.somenteLeitura || this.carregando || this.bloqueio || this.erro) return false;
    const ids = [...new Set((this.detalhe?.vinculosNota ?? []).map(v => v.idNFeEntrada))];
    if (!ids.length) return true;
    const travas = await firstValueFrom(this.notasService.consultarEdicoesVinculo(ids).pipe(takeUntil(this.destroy$)));
    if (travas.isError) throw new Error(travas.errorDescription);
    const bloqueada = travas.lista.find(t => t.isBloqueado);
    if (bloqueada) { this.bloqueio = bloqueada.motivoBloqueio; return false; }
    return true;
  }

  /** Botão da faixa de vínculo de uma NF já vinculada ou nota escolhida no seletor (troca de NF). */
  async abrirVinculo(idNota: number): Promise<void> {
    let isRecarregar = false;
    try {
      if (!await this.verificarTrava()) return;
      this.carregando = true;
      // Com associação pendente, o fluxo abre antes o modal de associação; mesmo sem concluir o
      // vínculo, a associação pode ter sido gravada: o painel é recarregado.
      const contextoAtual = this.contextos.find(c => c.nota.idNFeEntrada === idNota);
      isRecarregar = !!contextoAtual && this.isAssociacaoPendente(contextoAtual);
      const gerenciandoAtual = (this.detalhe?.vinculosNota ?? []).some(v => v.idNFeEntrada === idNota);
      const vinculado = await this.vinculoNotaPedidoFluxo.executar(idNota, contexto => ({
        idsCompra: gerenciandoAtual ? contexto.vinculosPedido.map(v => v.idCompra) : this.card.idCompra ? [this.card.idCompra] : [],
        idsVinculoSubstituir: gerenciandoAtual ? contexto.vinculosPedido.map(v => v.idCompraNFeEntradaVinculo)
          : (this.detalhe?.vinculosNota ?? []).map(v => v.idCompraNFeEntradaVinculo),
      }));
      if (vinculado && !this.destruido) { this.atualizado.emit(); isRecarregar = false; }
    } catch (e) { this.exibirErro(e); }
    finally { this.carregando = false; }
    if (isRecarregar && !this.destruido) await this.carregarNotasVinculadas();
  }

  private exibirErro(e: unknown): void {
    if (!this.destruido) this.alert.show('NF do pedido', e instanceof Error ? e.message : 'Não foi possível executar a operação.', AlertType.error);
  }
}
